import { Injectable } from '@angular/core';
import { ApiAuthService } from './api-auth.service';

export interface AddStockDto {
  productId: number;
  partnerId: number;
  quantity: number;
  productVariantId?: number;
  unitCost?: number;
  batchNumber?: string;
  expiryDate?: string;
  note?: string;
}

export interface UseStockDto {
  productId: number;
  partnerId: number;
  quantity: number;
  productVariantId?: number;
  deliveryId?: number;
}

export interface PaginatedStockResponse<T> {
  data: T[];
  count: number;
}

export interface StockFilters {
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  type?: string;
  productVariantId?: number;
  page?: number;
  limit?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ProductStocksService extends ApiAuthService {
  private readonly url = `${this.baseUrl}/product-stocks`;

  async addStock(params: AddStockDto): Promise<any> {
    return this.request(() =>
      this.http.post<any>(`${this.url}/add`, params).toPromise()
    );
  }

  async useStock(params: UseStockDto): Promise<any[]> {
    return this.request(() =>
      this.http.post<any[]>(`${this.url}/use`, params).toPromise()
    );
  }

  async consumeDeliveryStock(deliveryId: number): Promise<any> {
    return this.request(() =>
      this.http.post<any>(`${this.url}/delivery/${deliveryId}/consume`, {}).toPromise()
    );
  }

  async getAvailableStock(
    productId: number,
    partnerId: number,
    productVariantId?: number
  ): Promise<number> {
    const params: any = {};

    if (productVariantId) {
      params.productVariantId = productVariantId;
    }

    return this.request(() =>
      this.http.get<number>(
        `${this.url}/available/${productId}/${partnerId}`,
        { params }
      ).toPromise()
    );
  }

  async getStockLines(
    productId: number,
    partnerId: number,
    filters: StockFilters = {},
  ): Promise<PaginatedStockResponse<any>> {
    const params: any = {};

    if (filters.productVariantId) params.productVariantId = filters.productVariantId;
    if (filters.search) params.search = filters.search;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.status) params.status = filters.status;
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;

    return this.request(() =>
      this.http.get<PaginatedStockResponse<any>>(
        `${this.url}/lines/${productId}/${partnerId}`,
        { params },
      ).toPromise(),
    );
  }

  async getStockMovements(
    productId: number,
    partnerId: number,
    filters: StockFilters = {},
  ): Promise<PaginatedStockResponse<any>> {
    const params: any = {};

    if (filters.productVariantId) params.productVariantId = filters.productVariantId;
    if (filters.search) params.search = filters.search;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.type) params.type = filters.type;
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;

    return this.request(() =>
      this.http.get<PaginatedStockResponse<any>>(
        `${this.url}/movements/${productId}/${partnerId}`,
        { params },
      ).toPromise(),
    );
  }
}